import { isEqual } from 'lodash';

import { useSharedApi } from '@goodt-wcore/utils';
import { ProjectDataProviderRepository } from '@goodt-common/dremio';

import { ToastMessage } from '../const';
import { ValidationError } from '@goodt-common/layers';
import { DatasetJsonValidator } from '../validators/dataset-json-validator';
import { DatasetRequestValidator } from '../validators/dataset-props-validator';
import DatasetEditor from '../components/DatasetEditor.vue';
import { buildDatasetNameRe } from '../utils';

/**
 * @typedef {import('@goodt-common/data').IDatasetRequestProps} IDatasetRequestProps
 * @typedef {import('@goodt-common/data').IProjectDataProvider} IProjectDataProvider
 * @typedef {import('@goodt-wcore/components').ActionMenu.IAction} IAction
 * @typedef {{ datasetRequest: IDatasetRequestProps, actions: IAction[], error: string }} IdatasetRequestsBinds
 */

export default {
    components: {
        UiDatasetRequestEditor: null
    },
    inject: {
        $shared: {
            default: (vm) => useSharedApi(vm)
            // default: null
        }
    },
    props: {
        isMultiple: {
            type: Boolean,
            default: true
        }
    },
    provide() {
        const { isNameUnique: $isNameUnique } = this;
        return {
            $isNameUnique
        };
    },
    data: () => ({
        /**
         * @type {?{
         *     datasetRequest: null | IDatasetRequestProps,
         *     index: number
         * }}
         */
        edit: null
    }),
    computed: {
        /**
         * @return {IDatasetRequestProps[]}
         */
        datasetRequests() {
            /* should be overridden in consumer component */
        },
        datasetRequestsFiltered() {
            return this.datasetRequests;
        },
        /**
         * @return {IProjectDataProvider[]}
         */
        dataProviders() {
            return ProjectDataProviderRepository.get();
        },
        /**
         * @return {string|null}
         */
        dataProviderDefaultId() {
            const dataProvider = ProjectDataProviderRepository.getDefault();
            return dataProvider?.id ?? null;
        },
        /**
         * @return {IdatasetRequestsBinds[]}
         */
        datasetRequestsBinds() {
            return this.datasetRequestsFiltered.map(
                /**
                 * @param {IDatasetRequestProps} datasetRequest
                 * @param {number} index
                 * @return {{datasetRequest: IDatasetRequestProps}}
                 */
                (datasetRequest, index) => ({
                    datasetRequest,
                    actions: this.buildActions(index),
                    error: this.resolveError(datasetRequest)
                })
            );
        }
    },
    beforeCreate() {
        const vm = this;
        this.$options.components.UiDatasetRequestEditor = {
            functional: true,
            render(h) {
                const { edit, dataProviders, dataProviderDefaultId } = vm;
                if (edit == null) {
                    return null;
                }
                return h(DatasetEditor, {
                    props: {
                        dataProviders,
                        dataProviderDefaultId,
                        dataset: edit.datasetRequest
                    },
                    on: {
                        save: (...args) => vm.onDatasetEditorSave(...args),
                        close: () => vm.onDatasetEditorClose()
                    }
                });
            }
        };
    },
    methods: {
        buildActions(index) {
            return [
                {
                    name: 'Копировать JSON',
                    icon: 'content-copy',
                    func: () => this.onDatasetRequestPropsCopy(this.datasetRequests[index])
                },
                {
                    name: 'Перезаписать JSON',
                    icon: 'repeat@msi',
                    func: () => this.onDatasetRequestPropsImport(index)
                },
                null,
                {
                    name: 'Удалить',
                    icon: 'delete-outline',
                    class: 'color-red',
                    func: () => this.onDatasetDelete(index)
                }
            ];
        },
        /**
         * @param {string} name
         * @param {number} index
         * @return {boolean}
         */
        isNameUnique(name, index = this.edit?.index ?? -1) {
            const { datasetRequests } = this;
            const foundIndex = datasetRequests.findIndex(({ name: _name }) => name === _name);
            return foundIndex === -1 || foundIndex === index;
        },
        /**
         * @param {IDatasetRequestProps} datasetRequest
         * @return {string|null}
         */
        resolveError(datasetRequest) {
            const { isError, error } = new DatasetRequestValidator().validate(datasetRequest);
            if (isError) {
                return error.message;
            }
            return null;
        },
        saveDatasets(datasetRequestsNew) {
            this.datasetRequests = datasetRequestsNew;
        },
        ensureNameUnique(name) {
            let baseName, index;

            const init = () => {
                baseName = name;
                index = 1;

                const nameMatch = name.match(buildDatasetNameRe());
                if (nameMatch != null) {
                    baseName = nameMatch[1];
                }

                const re = buildDatasetNameRe(baseName);
                const postfixes = this.datasetRequests.map(({ name }) => Number(name.match(re)?.[2])).filter(Boolean);

                if (postfixes.length > 0) {
                    index = Math.max(...postfixes) + 1;
                }
            };

            while (!this.isNameUnique(name, -1)) {
                if (index == null) {
                    init();
                }
                name = `${baseName} (${index})`;
                index++;
            }

            return name;
        },
        /**
         *
         * @param {string} json
         * @param {number} index
         * @return {[IDatasetRequestProps | null, boolean]}
         */
        ensureDatasetRequestJson(json, index) {
            const toastError = (errorMessage, isError = true) =>
                this.$shared.toast(errorMessage, { type: isError ? 'error' : null });

            let { isError, error, result: datasetRequest } = new DatasetJsonValidator(json).validate();

            if (isError) {
                toastError(
                    error instanceof ValidationError
                        ? this.$tCap(error.message)
                        : ToastMessage.IMPORT_FROM_CLIPBOARD_INVALID_JSON()
                );
                return [null, false];
            }

            const checkingIndex = this.isMultiple ? index : 0;
            if (isEqual(datasetRequest, this.datasetRequests[checkingIndex])) {
                toastError(ToastMessage.IMPORT_FROM_CLIPBOARD_JSON_EQUAL(), false);
                return [null, false];
            }

            let nonCriticalErrorMessage = this.resolveError(datasetRequest);
            if (nonCriticalErrorMessage) {
                toastError(
                    ToastMessage.DATASET_PROPS_IMPORTED_WITH_ERRORS({
                        name: datasetRequest.name,
                        error: nonCriticalErrorMessage
                    })
                );
            }
            return [datasetRequest, nonCriticalErrorMessage == null];
        },
        /**
         * @param {import('../types').IDatasetRequestProps} datasetRequest
         */
        onDatasetEditorSave(datasetRequest) {
            const {
                datasetRequests,
                edit: { index }
            } = this;
            const datasetRequestsMutable = [...datasetRequests];
            if (index === -1) {
                datasetRequestsMutable.push(datasetRequest);
            } else {
                datasetRequestsMutable.splice(index, 1, datasetRequest);
            }
            this.saveDatasets(datasetRequestsMutable);
            this.onDatasetEditorClose();
            const text = ToastMessage[index === -1 ? 'DATASET_PROPS_ADDED' : 'DATASET_PROPS_UPDATED']({
                name: datasetRequest.name
            });
            this.$shared.toast(text);
        },
        //
        onDatasetCreate() {
            this.edit = {
                index: -1,
                datasetRequest: null
            };
        },
        /**
         * @param {import('../types').IDatasetRequestProps} datasetRequest
         * @param {number} index
         */
        onDatasetEdit(datasetRequest, index) {
            this.edit = {
                index,
                datasetRequest
            };
        },
        onDatasetEditorClose() {
            this.edit = null;
        },
        /**
         * @param {number} index
         */
        onDatasetDelete(index) {
            const datasetRequestsMutable = [...this.datasetRequests];
            const [deleted] = datasetRequestsMutable.splice(index, 1);
            // reassign is MANDATORY instead of array mutation for watcher
            this.saveDatasets(datasetRequestsMutable);
            this.$shared.toast(ToastMessage.DATASET_PROPS_DELETED({ name: deleted.name }));
        },
        /**
         * @param {IDatasetRequestProps} datasetRequest
         * @param {?number} [index = null]
         * @param {boolean} [showToast=true]
         */
        onDatasetImport(datasetRequest, index = null, showToast = true) {
            // reassign is MANDATORY instead of array mutation (push) for watcher
            const datasetRequestsMutable = [...this.datasetRequests];

            let startIndex = 0;
            let deleteCount = 1;
            if (this.isMultiple) {
                datasetRequest.name = this.ensureNameUnique(datasetRequest.name);
                startIndex = index == null ? datasetRequestsMutable.length : index;
                deleteCount = index == null ? 0 : 1;
            }
            // const shouldAdd = index == null || this.isMultiple;
            const [replaced] = datasetRequestsMutable.splice(startIndex, deleteCount, datasetRequest);

            this.saveDatasets(datasetRequestsMutable);
            if (showToast) {
                const text =
                    index === null
                        ? ToastMessage.DATASET_PROPS_ADDED({ name: datasetRequest.name })
                        : ToastMessage.DATASET_PROPS_REPLACED({ newName: datasetRequest.name, oldName: replaced.name });
                this.$shared.toast(text);
            }
        },
        /**
         * @param {IDatasetRequestProps} datasetRequest
         */
        onDatasetRequestPropsCopy(datasetRequest) {
            const message = ToastMessage.DATASET_PROPS_COPIED({ name: datasetRequest.name });
            this.$shared.clipboard(datasetRequest, { message });
        },
        /**
         * @param {?number} index
         * @return {Promise<void>}
         */
        async onDatasetRequestPropsImport(index) {
            const jsonString = await navigator.clipboard.readText();
            const [datasetRequest, hasError] = this.ensureDatasetRequestJson(jsonString, index);
            if (datasetRequest != null) {
                this.onDatasetImport(datasetRequest, index, hasError === false);
            }
        }
    }
};
